import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { parseRupees, type PayableStage, type ResolvedRate } from '@tailoring-crm/shared';
import { formatCurrency } from '../../lib/format.js';
import { cn, Button, Input } from '@tailoring-crm/ui';

/**
 * The rate grid: garment types down, stages across.
 *
 * The hard part of this screen is not the table, it is making the FALLBACK legible. A cell
 * showing ₹250 means one of three quite different things — this person is paid ₹250, or the
 * shop pays ₹250 and this person is not special, or nobody has priced it at all — and an owner
 * who cannot tell them apart will either double-set rates they did not need to or assume work
 * is priced when it is not. So an inherited cell is visibly quieter than an own rate, an unset
 * cell says "not set" rather than showing ₹0, and the legend names all three.
 *
 * On a phone the grid scrolls sideways inside its own container with the garment column pinned,
 * rather than the page scrolling horizontally.
 */

export interface RateMatrixProps {
  matrix: ResolvedRate[];
  stages: readonly PayableStage[];
  /** Absent on a read-only view — which is what a tailor looking at their own rates gets. */
  onSetRate?: (input: { garmentTypeKey: string; stage: PayableStage; ratePaise: number }) => void;
  /** Offered only on a person's grid, where an override exists to be removed. */
  onClearRate?: (input: { garmentTypeKey: string; stage: PayableStage }) => void;
  pending?: boolean;
}

interface EditingCell {
  garmentTypeKey: string;
  stage: PayableStage;
}

export function RateMatrix({
  matrix,
  stages,
  onSetRate,
  onClearRate,
  pending = false,
}: RateMatrixProps): ReactElement {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<EditingCell | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const rows = useMemo(() => {
    const byGarment = new Map<string, { label: string; cells: Map<string, ResolvedRate> }>();
    for (const cell of matrix) {
      const row = byGarment.get(cell.garmentTypeKey) ?? {
        label: cell.garmentTypeLabel,
        cells: new Map<string, ResolvedRate>(),
      };
      row.cells.set(cell.stage, cell);
      byGarment.set(cell.garmentTypeKey, row);
    }
    return [...byGarment.entries()].map(([key, row]) => ({ key, ...row }));
  }, [matrix]);

  const startEditing = (cell: ResolvedRate): void => {
    setEditing({ garmentTypeKey: cell.garmentTypeKey, stage: cell.stage });
    // Pre-filled with what it resolves to today, including an inherited value — the common
    // edit is "the shop pays 250, he gets 300", not "type it from nothing".
    setDraft(cell.ratePaise === null ? '' : String(cell.ratePaise / 100));
    setError(null);
  };

  const commit = (): void => {
    if (!editing || !onSetRate) return;
    let ratePaise: number;
    try {
      ratePaise = parseRupees(draft);
    } catch {
      setError(t('staff.rates.invalidAmount'));
      return;
    }
    if (ratePaise < 0) {
      setError(t('staff.rates.invalidAmount'));
      return;
    }
    onSetRate({ garmentTypeKey: editing.garmentTypeKey, stage: editing.stage, ratePaise });
    setEditing(null);
    setDraft('');
  };

  return (
    <div className="flex flex-col gap-3">
      {/* The wide grid scrolls inside itself; the page never scrolls sideways. */}
      <div className="-mx-4 overflow-x-auto px-4">
        <table className="w-full min-w-[36rem] border-collapse text-sm">
          <caption className="sr-only">{t('staff.rates.tableCaption')}</caption>
          <thead>
            <tr>
              <th scope="col" className="sticky left-0 bg-background p-2 text-left font-medium">
                {t('staff.rates.garment')}
              </th>
              {stages.map((stage) => (
                <th key={stage} scope="col" className="p-2 text-right font-medium">
                  {t(`stage.${stage}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-t border-border">
                <th scope="row" className="sticky left-0 bg-background p-2 text-left font-medium">
                  {row.label}
                </th>
                {stages.map((stage) => {
                  const cell = row.cells.get(stage);
                  const isEditing = editing?.garmentTypeKey === row.key && editing.stage === stage;

                  if (!cell) return <td key={stage} className="p-2" />;

                  if (isEditing) {
                    return (
                      <td key={stage} className="p-1 align-top">
                        <div className="flex flex-col items-end gap-1">
                          <Input
                            aria-label={t('staff.rates.cellLabel', {
                              garment: row.label,
                              stage: t(`stage.${stage}`),
                            })}
                            type="text"
                            inputMode="decimal"
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                commit();
                              }
                              if (event.key === 'Escape') setEditing(null);
                            }}
                            className="h-9 w-24 text-right"
                            autoFocus
                          />
                          <div className="flex gap-1">
                            <Button size="sm" onClick={commit} disabled={pending}>
                              {t('common.save')}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                              {t('common.cancel')}
                            </Button>
                          </div>
                          {error ? <p className="text-xs text-destructive">{error}</p> : null}
                        </div>
                      </td>
                    );
                  }

                  const label = t('staff.rates.cellLabel', {
                    garment: row.label,
                    stage: t(`stage.${stage}`),
                  });

                  return (
                    <td key={stage} className="p-1 text-right">
                      {onSetRate ? (
                        <button
                          type="button"
                          onClick={() => startEditing(cell)}
                          aria-label={label}
                          className={cn(
                            'w-full rounded-md px-2 py-2 text-right tabular-nums transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                            // An inherited rate is deliberately quieter than an own one.
                            cell.source === 'shop_default' && 'text-muted-foreground',
                            cell.ratePaise === null && 'text-muted-foreground',
                          )}
                        >
                          {cell.ratePaise === null
                            ? t('staff.rates.notSet')
                            : formatCurrency(cell.ratePaise)}
                        </button>
                      ) : (
                        <span
                          className={cn(
                            'block px-2 py-2 tabular-nums',
                            cell.source === 'shop_default' && 'text-muted-foreground',
                            cell.ratePaise === null && 'text-muted-foreground',
                          )}
                        >
                          {cell.ratePaise === null
                            ? t('staff.rates.notSet')
                            : formatCurrency(cell.ratePaise)}
                        </span>
                      )}
                      {onClearRate && cell.source === 'staff' ? (
                        <button
                          type="button"
                          onClick={() => onClearRate({ garmentTypeKey: row.key, stage })}
                          className="px-2 text-xs text-muted-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {t('staff.rates.useShopRate')}
                        </button>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Three states, three meanings — named, because a number alone cannot say which. */}
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <li>{t('staff.rates.legend.own')}</li>
        <li>{t('staff.rates.legend.inherited')}</li>
        <li>{t('staff.rates.legend.unset')}</li>
      </ul>
    </div>
  );
}
